import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from "react-router";
import { ApplicationState } from '../../../store';
import { SharedMaterialAccessType, SharedMaterialsFullCompany } from '../../../store/Models/SharedMaterials/DataModel';
import { SurveyResponseModel, SurveyResponseOfflineProcessStatus } from "../../../store/Models/Surveys/SurveyDataModel";
import { TeamRole } from "../../../store/Models/UserContext";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { SurveyResponseState } from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";
import { ToxnotComponentProperties } from "../../core/BaseApplicationComponents/ToxnotComponent";
import { MainApplicationViewState, ToxnotMainApplicationViewComponent, ViewOptions } from '../../core/BaseApplicationComponents/ToxnotMainApplicationViewComponent';
import { DropdownMenu } from '../../core/DropdownMenu';
import { RadioButton } from '../../core/RadioButton';
import TextInput from '../../core/TextInput';
import { IntercomManager } from '../../../utilities/IntercomManger';

import "./styles.css";

enum SurveyResponseSubmitStateUiState {
    Loading,
    Failed,
    Loaded,
    Submitted
}

interface SurveyResponseSubmitState extends MainApplicationViewState<SurveyResponseSubmitStateUiState> {
    companyName: string;
    selectedCompanyId: string | null;
    passportPermissionLevel: SharedMaterialAccessType;
    teamBrands: SharedMaterialsFullCompany[];
}

interface SurveyResponseSubmitProps {
    onGoBackButtonClick: () => void;
}

type SurveyResponseProperties =
    SurveyResponseSubmitProps &
    ToxnotComponentProperties &
    RouteComponentProps<{ responseId?: string }> &
    SurveyResponseState &
    typeof SurveyResponseController.actionCreators;

class SurveyResponseSubmit extends ToxnotMainApplicationViewComponent<
    SurveyResponseProperties,
    SurveyResponseSubmitStateUiState,
    SurveyResponseSubmitState,
    any> {

    constructor(props: SurveyResponseProperties) {
        super(props);

        this.state = {
            selectedCompanyId: null,
            companyName: "",
            teamBrands: [],
            passportPermissionLevel: SharedMaterialAccessType.Protected,
            uiState: SurveyResponseSubmitStateUiState.Loading
        };
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    protected saveDataToService(data: any): Promise<boolean> {
        throw new Error('Method not implemented.');
    }

    protected getPostInitialLoadUiState(): SurveyResponseSubmitStateUiState {
        return this.hasResponseBeenSubmittedOrIsBeingSubmitted() ? SurveyResponseSubmitStateUiState.Submitted : SurveyResponseSubmitStateUiState.Loaded;
    }

    protected async fetchViewDataAndRefreshState(): Promise<void> {
        const result = await this._serviceClient.sharedMaterialsCompaniesGet();

        if (!result.success || result.data === null) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        this.setState({
            teamBrands: result.data,
            companyName: result.data.length === 1 ? result.data[0].name : "",
            selectedCompanyId: result.data.length === 1 ? result.data[0].companyId : null,
        });
    }

    protected getLocalizedViewTitle(): string | null {
        return null;
    }

    protected getViewOptions(): Partial<ViewOptions> {
        return {
            renderAppLayout: false
        }
    }

    private renderContent() {
        let content: JSX.Element;

        const shouldPublishAsPassport = this.props.coreData!.request?.dataRequested.anyPassportDataAsked;

        if (this.state.uiState === SurveyResponseSubmitStateUiState.Submitted) {
            content = this.renderSubmitted();
        } else if (shouldPublishAsPassport) {
            content = (
                <>
                    <div className="content-description-container">
                        {this.localizeString("Your response will create a Digital Product Passport. You can choose to share the Passport just with your requestor or make it public on our supply chain database.")}
                    </div>
                    <div className="submit-access-levels-container">
                        <div key={SharedMaterialAccessType.Public.toString()} className="submit-access-level-option">
                            <RadioButton
                                value={SharedMaterialAccessType.Public.toString()}
                                name={this.localizeString("Public")}
                                selected={this.state.passportPermissionLevel === SharedMaterialAccessType.Public}
                                onValueChange={v => this.setState({ passportPermissionLevel: v as SharedMaterialAccessType })} />
                            <p>
                                {this.localizeString("Anyone can search and see my data.")}
                            </p>
                        </div>
                        <div key={SharedMaterialAccessType.Protected.toString()} className="submit-access-level-option">
                            <RadioButton
                                value={SharedMaterialAccessType.Protected.toString()}
                                name={this.localizeString("Restricted")}
                                selected={this.state.passportPermissionLevel === SharedMaterialAccessType.Protected}
                                onValueChange={v => this.setState({ passportPermissionLevel: v as SharedMaterialAccessType })} />
                            <p>
                                {this.localizeString("Share only with my requestor. People can search but cannot see my data.")}
                            </p>
                        </div>
                        <div key={SharedMaterialAccessType.Private.toString()} className="submit-access-level-option">
                            <RadioButton
                                value={SharedMaterialAccessType.Private.toString()}
                                name={this.localizeString("Private")}
                                selected={this.state.passportPermissionLevel === SharedMaterialAccessType.Private}
                                onValueChange={v => this.setState({ passportPermissionLevel: v as SharedMaterialAccessType })} />
                            <p>
                                {this.localizeString("Share only with my requestor. People cannot search or see my data.")}
                            </p>
                        </div>
                    </div>
                    <div className="submit-brand-selector-container">
                        <p>
                            {this.localizeString("Brand Name")}
                        </p>
                        {this.state.teamBrands.length < 2
                            ?
                            <TextInput placeholder={this.localizeString("Enter Your Brand Name")} value={this.state.companyName} onValueChange={v => this.setState({ companyName: v })} />
                            :
                            <DropdownMenu
                                options={this.state.teamBrands}
                                getUniqueValueForOption={(c: SharedMaterialsFullCompany) => encodeURIComponent(c.companyId)}
                                getFriendlyNameForOptions={(c: SharedMaterialsFullCompany) => c.name.emptyCoalesce(this.localizeString("Unamed Brand"))}
                                onOptionSelected={(c: SharedMaterialsFullCompany) => this.setState({ selectedCompanyId: c.companyId, companyName: this.state.teamBrands.firstOrDefault(b => b.companyId === c.companyId)?.name ?? "" })}
                            />
                        }
                    </div>
                </>
            );
        } else {
            content = (
                <div className="start-subcontent-content-container">
                    <p>
                        {this.localizeString("You're almost there, by clicking submit your response will be sent to your requestor.")}
                    </p>
                </div>
            );
        }

        return (
            <div className={`content ${this.state.uiState !== SurveyResponseSubmitStateUiState.Submitted ? "p-r-15" : ""}`}>
                {content}
                <div className="submit-response-submit-button-container">
                    {this.state.uiState !== SurveyResponseSubmitStateUiState.Submitted &&
                        <button className="btn btn-default m-r-5" onClick={e => this.props.onGoBackButtonClick()}>{this.localizeString("Go Back")}</button>
                    }
                    {this.state.uiState === SurveyResponseSubmitStateUiState.Submitted &&
                        <>
                            <button data-dismiss="modal" className="btn btn-default m-r-5">{this.localizeString("Go Back")}</button>
                            <a href={`/Exchange/Database/Supplier/${this.getUserContextForSignedInUser().teamOwnerId}`} className="btn btn-primary m-r-5">{this.localizeString("See Passport")}</a>
                        </>
                    }
                    {this.state.uiState !== SurveyResponseSubmitStateUiState.Submitted && <button className="btn btn-toxnot-primary m-r-5" onClick={e => this.submitButton_click()} disabled={!this.canSubmit()}>{this.localizeString("Submit")}</button>}
                </div>
            </div>
        );
    }

    private renderSubmitted() {
        return (
            <div className="alert alert-success m-b-15">
                {this.localizeString("Your response was successfully submitted!")}
            </div>
        );
    }

    private renderInfo() {
        const shouldRender = this.props.coreData!.request?.dataRequested.anyPassportDataAsked &&
            (this.state.uiState !== SurveyResponseSubmitStateUiState.Submitted);

        return (
            <div className="info">
                {shouldRender && <img src="/images/survey/survey-response-submit-dpp-info.png" />}
            </div>
        );
    }

    protected renderViewContent(): JSX.Element | null {
        return (
            <div className="survey-response-submit-root">
                {this.renderContent()}
                {this.renderInfo()}
            </div>
        );
    }

    private getOfflineResponseStatusFromResponse(response: SurveyResponseModel): SurveyResponseOfflineProcessStatus {
        const defaultStatus: SurveyResponseOfflineProcessStatus = {
            isOfflineProcessStarted: false
        };

        try {
            if (response.responseDataJson === null) {
                return defaultStatus;
            }
            const status = JSON.parse(response.responseDataJson);

            return status;
        }
        catch {
            return defaultStatus;
        }
    }

    private hasResponseBeenSubmittedOrIsBeingSubmitted = () => (
        this.getOfflineResponseStatusFromResponse(this.props.coreData!.response).isOfflineProcessStarted ||
        this.props.coreData?.response.state === 1 // submitted
    );

    private canSubmit = () => (SurveyResponseController.isOverallValid(this.props.validationResult) || !this.props.coreData?.request?.requireValidationOnResponse) && !this.hasResponseBeenSubmittedOrIsBeingSubmitted();

    private submitButton_click = async () => {

        if (this.props.coreData?.request?.requireValidationOnResponse &&
            !SurveyResponseController.isOverallValid(this.props.validationResult) &&
            (this.props.coreData?.extensionRequest?.inventoryIds ?? []).length === 0) {

            alert(this.localizeString("Please complete all sections of the survey before attempting to submit."));
            return;
        }

        const shouldPublishAsPassport = this.props.coreData!.request?.dataRequested.anyPassportDataAsked;

        if (shouldPublishAsPassport && this.state.companyName === "" && this.state.selectedCompanyId === null) {
            alert(this.localizeString("Please enter or select a brand name to publish your passport under."));
            return;
        }

        // Should never be true if we get to the button handler, but just making sure.
        if (!this.canSubmit()) {
            return;
        }

        if (!this.props.match.params.responseId) {
            return;
        }

        this.setState({
            uiState: SurveyResponseSubmitStateUiState.Loading
        });

        const result = await this._serviceClient.submitDataCollectionResponse(
            this.props.match.params.responseId,
            true,
            this.state.selectedCompanyId,
            this.state.companyName,
            this.state.passportPermissionLevel
        );

        if (!result.success || result.data === null) {
            this.enterFailedState();
            return;
        }

        this.setState({
            uiState: SurveyResponseSubmitStateUiState.Submitted,
        });

        this.props.updateSurveyResponseToSubmitted();

        IntercomManager.TrackEvent(this.props.appServices, "submit-survey-response");
    }
}

export default connect(
    (state: ApplicationState, ownProps: SurveyResponseSubmitProps) => ({ ...state.surveyResponseState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(withRouter(SurveyResponseSubmit) as any);
